import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import Info from '@mui/icons-material/Info';
import Warning from '@mui/icons-material/Warning';
import Autocomplete from '@mui/material/Autocomplete';
import { type BoxProps } from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/material/styles/useTheme';
import { useEffect, useState } from 'react';
import Badge from '../../components/Badge/Badge';
import { Effizienz, type EffizienzClass } from '../../components/effizienz';
import Iconify from '../../components/iconify/component';
import { type IconifyProps } from '../../components/iconify/types';
import { type CustomTheme } from '../../provider/ThemeProvider';
import { LEAD } from '../../vars';
import ImageEl from '../Image';
import { getAllFieldFromArray, getElementFromArray } from '../Texte';
import { checkUndefined, checkUndefinedNullOrEmpty, checkUndefinedOrNull } from '../Tools';
import InputNonEditValue from './helpers/render-non-edit-value';

export type SelectDataObject<T = object> = {
    badge?: string;
    effizienz?: { wltp: boolean };
    disabled?: boolean;
    et?: string;
    group?: string;
    hidden?: boolean;
    i?: string | number | boolean | (string | number | boolean)[];
    icon?: IconifyProps | '-';
    iconAutoSizeHeight?: string;
    iconColor?: BoxProps['color'];
    img?: string;
    imgRatio?: [number, number];
    t?: string;
    warning?: string;
    warningSPT?: string;
} & T;

export type InputSelectProps = {
    /** Disable FullWidth */
    autoWidth?: boolean;
    /** Name for AutoFill */
    autoComplete?: string;
    /** Enable Blink-Animation */
    blink?: boolean;
    /** Callback-Handler for Input */
    callback?: (params: { id: string; val: SelectDataObject['i'] | any }) => void;
    /** CSS Classname */
    className?: string;
    /** Data for Select (Format { i: Value, t: Label (optional) }) */
    data?: SelectDataObject[];
    /** Debug */
    debug?: boolean;
    /** Disable Input */
    disabled?: boolean;
    /** Show Input-Elements, else show value only */
    edit?: boolean;
    /** Show Icon at End */
    endIcon?: IconifyProps;
    /** Show Error-Message */
    error?: string;
    /** Toggle fat Label */
    fatLabel?: boolean;
    /** Custom Height */
    height?: string;
    /** Additional Text for Help */
    helperText?: string;
    /** Hide Label in non-Edit-Mode */
    hideLabel?: boolean;
    /** Hide Label in Edit-Mode */
    hideLabelEdit?: boolean;
    /** Icon for Label */
    icon?: IconifyProps;
    /** ID for Callback-Handler */
    id?: string;
    /** Info-Icon */
    info?: string;
    /** Label */
    l?: string;
    /** Show Loading Animation */
    loading?: boolean;
    /** Disable Groupless Entries at Bottom */
    noGroupAtTop?: boolean;
    /** Don't sort options */
    noOptionSort?: boolean;
    /** Text, if no option is available */
    noOptionsText?: string;
    /** Custom OnClick-Function */
    onClick?: (e: any) => void;
    /** Custom onKeyUp-Function */
    onKeyUp?: () => void;
    /** Additional OnType-Function */
    onType?: (e: any) => void;
    /** Readonly mode */
    readOnly?: boolean;
    /** Show et from Data */
    showExtra?: boolean;
    /** Show another Field from Data */
    showField?: string;
    /** Only Send Changed IDs to Callback */
    showOnlyChanges?: boolean;
    /** ID for Testing */
    testid?: string;
    /** Custom TextAlign */
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    /** Type */
    type?: 'singleselect' | 'multiselect' | 'radio';
    /** Use ID "parent" from Data */
    useParent?: boolean;
    /** Initial Value */
    v?: SelectDataObject['i'] | any;
    /** Value after Change */
    vNew?: SelectDataObject['i'] | any;
};

export function convertSelectValue(
    v: InputSelectProps['v'],
    data: InputSelectProps['data'],
    type: InputSelectProps['type'],
    showExtra: InputSelectProps['showExtra'],
    showField: InputSelectProps['showField'],
    height: InputSelectProps['height'],
    testid: InputSelectProps['testid'],
    id: InputSelectProps['id']
): string {
    const finalId = !checkUndefinedNullOrEmpty(testid) ? testid : id;
    let val = undefined;
    if (type === 'multiselect') {
        val = [];
        if (!checkUndefinedNullOrEmpty(v)) {
            for (let i = 0; i < (data || []).length; i++) {
                if (v.includes(data[i]?.i)) val.push(data[i]?.t);
            }
        } else val = ['-'];
        if (!checkUndefinedNullOrEmpty(val)) {
            val = val.filter((t) => !checkUndefinedNullOrEmpty(t)).reduce((prev, curr) => [prev, ', ', curr]);
        }
    } else {
        if (!checkUndefinedNullOrEmpty(v)) {
            for (let i = 0; i < (data || []).length; i++) {
                const option = data[i];
                if (option.i === v) {
                    if ((checkUndefinedNullOrEmpty(showField) || showField === 'effizienz') && option.effizienz) {
                        val = (
                            <Tooltip key={`inputSelect${finalId}_selectedOptionIconTooltip${option.i}`} title={option.t}>
                                <span key={`inputSelect${finalId}_selectedOptionIconSpan${option.i}`} style={{ fontSize: height }}>
                                    <Effizienz
                                        type="step"
                                        stepHeight={typeof height === 'string' ? (height?.endsWith('em') ? parseFloat(height?.replace('em', '')) * 16 : 16) : height}
                                        step={option.i as EffizienzClass}
                                        wltp={option.effizienz.wltp}
                                    />
                                </span>
                            </Tooltip>
                        );
                    } else if ((checkUndefinedNullOrEmpty(showField) || showField === 'icon') && option.icon) {
                        val = (
                            <>
                                <Tooltip key={`inputSelect${finalId}_selectedOptionIconTooltip${option.i}`} title={option.t}>
                                    <span key={`inputSelect${finalId}_selectedOptionIconSpan${option.i}`} style={{ fontSize: height }}>
                                        <Iconify
                                            width={!checkUndefinedNullOrEmpty(option.iconAutoSizeHeight) ? 'auto' : undefined}
                                            height={option.iconAutoSizeHeight}
                                            color={option.iconColor}
                                            icon={option.icon}
                                        />
                                    </span>
                                </Tooltip>
                                {showExtra && option.et ? (
                                    <span key={`inputSelect${finalId}_selectedOptionIconText${option.i}`} style={{ fontSize: height, marginLeft: 5 }}>
                                        {option.et}
                                    </span>
                                ) : undefined}
                            </>
                        );
                    } else if ((checkUndefinedNullOrEmpty(showField) || showField === 'img') && option.img) {
                        val = (
                            <>
                                <ImageEl
                                    key={`inputSelect${finalId}_selectedOptionImage${option.i}`}
                                    contain
                                    height={height}
                                    externalImage
                                    disablePlaceholder
                                    ratio={option.imgRatio || [4, 3]}
                                    src={option.img}
                                />
                                {showExtra && option.et ? (
                                    <span key={`inputSelect${finalId}_selectedOptionImageText${option.i}`} style={{ fontSize: height, marginLeft: 5 }}>
                                        {option.et}
                                    </span>
                                ) : undefined}
                            </>
                        );
                    } else if ((checkUndefinedNullOrEmpty(showField) || showField === 'badge') && option.badge) {
                        val = (
                            <>
                                <Badge key={`inputSelect${finalId}_selectedOptionBadge${option.i}`} style={{ margin: 0 }} color={'grey'}>
                                    {option.badge}
                                </Badge>
                                {showExtra && option.et ? (
                                    <span key={`inputSelect${finalId}_selectedOptionBadgeText${option.i}`} style={{ fontSize: height, marginLeft: 5 }}>
                                        {option.et}
                                    </span>
                                ) : undefined}
                            </>
                        );
                    } else if ((checkUndefinedNullOrEmpty(showField) || showField === 't') && option.t) val = option.t + (showExtra && option.et ? ' ' + option.et : '');
                }
            }
        } else val = '-';
    }
    return val;
}

const InputSelect = (moduleProps: InputSelectProps) => {
    const {
        autoWidth,
        autoComplete,
        blink,
        callback,
        data,
        debug = false,
        disabled,
        edit,
        endIcon,
        error,
        fatLabel,
        height,
        helperText,
        hideLabel,
        hideLabelEdit,
        icon,
        id,
        info,
        l,
        loading,
        noGroupAtTop,
        noOptionSort,
        noOptionsText,
        onClick,
        onKeyUp,
        onType,
        readOnly,
        showExtra,
        showField,
        showOnlyChanges,
        testid,
        textAlign,
        type,
        useParent,
        v,
        vNew,
    }: InputSelectProps = {
        autoComplete: 'off',
        data: [],
        height: '1em',
        type: 'singleselect',
        ...moduleProps,
    };
    const [open, setOpen] = useState<boolean>(false);
    const [value, setValue] = useState<InputSelectProps['v']>(v);
    const theme: CustomTheme = useTheme();
    const finalId = !checkUndefinedNullOrEmpty(testid) ? testid : id;

    const getValue = (newVal) => {
        let val = null;
        if (edit) {
            if (type === 'multiselect') {
                val = (data || []).filter((option) => Array.isArray(newVal) && newVal.includes(option.i));
            } else if (!checkUndefinedNullOrEmpty(newVal) && !checkUndefinedNullOrEmpty(data)) {
                val = getElementFromArray(data, newVal, 'i', undefined);
            }
        } else {
            if (loading) {
                return '';
            } else val = convertSelectValue(newVal, data, type, showExtra, showField, height, testid, id);
        }
        return val;
    };

    const handleSubmit = (newVal) => {
        if (callback) callback({ id: id, val: newVal });
        else console.error('Callback-Funktion fehlt!\nInput-Key: inputSelect_' + id);
    };

    const handleChange = (e, newValue) => {
        let val;
        if (type === 'multiselect') {
            if (showOnlyChanges) {
                const workV = !checkUndefinedOrNull(value[0]) && typeof value[0] === 'object' ? getAllFieldFromArray(value, 'i', []) : !checkUndefined(value) ? value : null;
                if (newValue.length > workV.length) {
                    val = getAllFieldFromArray(newValue, 'i', []).filter((a) => !workV.includes(a));
                } else {
                    val = workV.filter((a) => !getAllFieldFromArray(newValue, 'i', []).includes(a));
                }
            } else val = getAllFieldFromArray(newValue, 'i', []);
        } else if (type === 'radio') val = !checkUndefinedOrNull(newValue) ? parseInt(newValue) : '';
        else val = useParent && !checkUndefinedOrNull(newValue.parent) ? newValue.parent : newValue.i;
        setValue(val);
        handleSubmit(val);
    };

    const heading = [];
    if (!hideLabel) {
        if (icon) {
            heading.push(
                <span key={'inputSelect' + finalId + '_headingIcon'} className="materialIcon iconMarginRight">
                    <Iconify icon={icon} />
                </span>
            );
        }
        if (l) heading.push(l);
    }

    useEffect(
        function () {
            setValue(!checkUndefined(vNew) ? vNew : v);
        },
        [v, vNew]
    );

    useEffect(
        function () {
            if (blink) {
                setTimeout(() => {
                    const element = document.getElementById(id);
                    if (!checkUndefinedOrNull(element)) {
                        element.scrollIntoView({ block: 'center' });
                    }
                }, 500);
            }
        },
        [blink, id]
    );

    const options: SelectDataObject[] = (data || []).filter((option) => !checkUndefinedNullOrEmpty(option) && !option.hidden);
    if (!noOptionSort) {
        options.sort((a, b) => {
            if (typeof a.i === 'string' && typeof b.i === 'string') {
                return (a.i || '').toLowerCase().localeCompare((b.i || '').toLowerCase());
            } else if (typeof a.i === 'number' && typeof b.i === 'number') {
                if ((a.i == -1 || a.i == 0) && a.i < b.i) return -1;
                else if ((b.i == -1 || b.i == 0) && b.i < a.i) return 1;
                else return (a.t || '').toLowerCase().localeCompare((b.t || '').toLowerCase());
            }
        });
        options.sort((a, b) => (a.group || (noGroupAtTop ? 'aaaaa' : 'zzzzz')).toLowerCase().localeCompare((b.group || (noGroupAtTop ? 'aaaaa' : 'zzzzz')).toLowerCase()));
    }

    return edit ? (
        <div
            key={'inputSelect' + finalId + '_div'}
            data-testid={finalId}
            className={[blink ? 'blinkInputfield' : '', 'inputSelect'].join(' ')}
            style={{ width: !autoWidth ? '100%' : undefined, marginBottom: !checkUndefinedOrNull(error) || !checkUndefinedOrNull(helperText) ? undefined : 10 }}
        >
            {type === 'radio' ? (
                <FormControl key={'inputSelect' + finalId + '_formcontrol'} fullWidth disabled={loading || disabled} error={!checkUndefinedOrNull(error)}>
                    <FormLabel
                        key={'inputSelect' + finalId + '_formlabel'}
                        disabled={loading || disabled}
                        style={{ color: !checkUndefinedOrNull(error) ? undefined : theme?.palette?.text?.primary, display: 'flex', alignItems: 'center' }}
                    >
                        {heading}
                        {!checkUndefinedOrNull(endIcon) && (
                            <div
                                key={'inputSelect' + finalId + '_endIcon'}
                                className="materialIcon"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined,
                                    marginLeft: 8,
                                    marginTop: 20,
                                    marginBottom: -20,
                                }}
                            >
                                <Iconify icon={endIcon} />
                            </div>
                        )}
                        {!checkUndefinedOrNull(info) && (
                            <div
                                key={'inputSelect' + finalId + '_info'}
                                className="materialIcon"
                                style={{ color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined, marginLeft: 8 }}
                            >
                                <Tooltip key={'inputSelect' + finalId + '_infoTooltip'} title={info}>
                                    <span key={'inputSelect' + finalId + '_infoSpan'}>
                                        <Info key={'inputSelect' + finalId + '_infoInfo'} color="primary" />
                                    </span>
                                </Tooltip>
                            </div>
                        )}
                    </FormLabel>
                    {!checkUndefinedOrNull(error) || !checkUndefinedOrNull(helperText) ? (
                        <FormHelperText key={'inputSelect' + finalId + '_formhelpertext'}>{!checkUndefinedOrNull(error) ? error : helperText}</FormHelperText>
                    ) : undefined}
                    <RadioGroup key={'inputSelect' + finalId + '_radioGroup'} aria-label={l} id={id} name={id} value={!checkUndefined(value) ? value : null} onChange={handleChange}>
                        {loading ? (
                            <>
                                <div key={'inputSelect' + finalId + '_radioGroupLoading1'} style={{ margin: '8px 0', display: 'flex', alignItems: 'center' }}>
                                    <Skeleton key={'inputSelect' + finalId + '_radioGroupLoading11'} animation="wave" className="iconMarginRight" width="calc(1em + 8px)" height="calc(1em + 8px)" />
                                    <Skeleton key={'inputSelect' + finalId + '_radioGroupLoading12'} animation="wave" width="100%" height="calc(1em + 8px)" />
                                </div>
                                <div key={'inputSelect' + finalId + '_radioGroupLoading2'} style={{ margin: '8px 0', display: 'flex', alignItems: 'center' }}>
                                    <Skeleton key={'inputSelect' + finalId + '_radioGroupLoading21'} animation="wave" className="iconMarginRight" width="calc(1em + 8px)" height="calc(1em + 8px)" />
                                    <Skeleton key={'inputSelect' + finalId + '_radioGroupLoading22'} animation="wave" width="100%" height="calc(1em + 8px)" />
                                </div>
                                <div key={'inputSelect' + finalId + '_radioGroupLoading3'} style={{ margin: '8px 0', display: 'flex', alignItems: 'center' }}>
                                    <Skeleton key={'inputSelect' + finalId + '_radioGroupLoading31'} animation="wave" className="iconMarginRight" width="calc(1em + 8px)" height="calc(1em + 8px)" />
                                    <Skeleton key={'inputSelect' + finalId + '_radioGroupLoading32'} animation="wave" width="100%" height="calc(1em + 8px)" />
                                </div>
                            </>
                        ) : (
                            (data || []).map(
                                (radio, index) =>
                                    radio &&
                                    !radio.hidden && (
                                        <FormControlLabel
                                            key={'inputSelect' + finalId + `_radioGroup${radio?.i}`}
                                            style={{ lineHeight: 1.2 }}
                                            disabled={disabled || radio.disabled}
                                            label={[
                                                radio.effizienz ? (
                                                    <span key={'inputSelect' + finalId + `_radioGroupIcon${radio?.i}`} style={{ fontSize: height }}>
                                                        <Effizienz
                                                            type="step"
                                                            stepHeight={typeof height === 'string' ? (height?.endsWith('em') ? parseFloat(height?.replace('em', '')) * 16 : 16) : height}
                                                            step={radio.i as EffizienzClass}
                                                            wltp={radio.effizienz.wltp}
                                                        />
                                                    </span>
                                                ) : (
                                                    ''
                                                ),
                                                radio.icon ? (
                                                    <span key={'inputSelect' + finalId + `_radioGroupIcon${radio?.i}`} style={{ fontSize: height }}>
                                                        <Iconify
                                                            width={!checkUndefinedNullOrEmpty(radio.iconAutoSizeHeight) ? 'auto' : undefined}
                                                            height={radio.iconAutoSizeHeight}
                                                            color={radio.iconColor}
                                                            icon={radio.icon}
                                                        />
                                                    </span>
                                                ) : (
                                                    ''
                                                ),
                                                radio.img ? (
                                                    <ImageEl
                                                        key={'inputSelect' + finalId + `_radioGroupImage${radio?.i}`}
                                                        contain
                                                        src={radio.img}
                                                        alt={radio.t}
                                                        ratio={radio.imgRatio || [4, 3]}
                                                        externalImage
                                                        disablePlaceholder
                                                        height={height}
                                                    />
                                                ) : (
                                                    ''
                                                ),
                                                !radio.img ? (
                                                    <Typography key={'inputSelect' + finalId + `_radioGroupText${radio?.i}`} variant="body1">
                                                        {radio.t}
                                                    </Typography>
                                                ) : (
                                                    ''
                                                ),
                                                radio.et ? (
                                                    <Typography key={'inputSelect' + finalId + `_radioGroupExtraText${radio?.i}`} style={{ fontSize: '0.75em' }} variant="body2">
                                                        {radio.et}
                                                    </Typography>
                                                ) : (
                                                    ''
                                                ),
                                            ]}
                                            control={
                                                <Radio
                                                    key={'inputSelect' + finalId + `_radioGroupRadio${radio?.i}`}
                                                    data-testid={'radio_' + id + '_' + index}
                                                    disabled={disabled || radio.disabled}
                                                    value={radio.i}
                                                    color="primary"
                                                />
                                            }
                                        />
                                    )
                            )
                        )}
                    </RadioGroup>
                </FormControl>
            ) : (
                <div
                    key={'inputSelect' + finalId + '_div'}
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: textAlign === 'right' ? 'flex-end' : textAlign === 'center' ? 'center' : textAlign === 'justify' ? 'space-between' : 'flex-start',
                    }}
                >
                    {(loading || icon) && (
                        <div
                            key={'inputSelect' + finalId + '_icon'}
                            className="materialIcon iconMarginRight"
                            style={{ color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined, marginTop: 20, marginBottom: -20 }}
                        >
                            {loading ? <CircularProgress key={'inputSelect' + finalId + '_iconLoading'} color="primary" size="1.5rem" /> : icon ? <Iconify icon={icon} /> : undefined}
                        </div>
                    )}
                    <Autocomplete
                        key={'inputSelect' + finalId + '_autocomplete'}
                        fullWidth
                        multiple={type === 'multiselect'}
                        options={options}
                        getOptionLabel={(option: string | SelectDataObject) =>
                            (option as SelectDataObject).t + (showExtra && (option as SelectDataObject).et ? ' ' + (option as SelectDataObject).et : '')
                        }
                        getOptionDisabled={(option: SelectDataObject) => option.disabled}
                        groupBy={(option: SelectDataObject) => option.group}
                        noOptionsText={noOptionsText || 'Keine Optionen gefunden'}
                        id={id}
                        value={!checkUndefined(value) ? getValue(value) : type === 'multiselect' ? [] : null}
                        disabled={loading || disabled}
                        disableClearable
                        disableCloseOnSelect={type === 'multiselect'}
                        open={open || debug}
                        readOnly={readOnly}
                        onOpen={onClick ? undefined : () => setOpen(true)}
                        onClose={() => setOpen(false)}
                        onChange={handleChange}
                        onKeyUp={
                            onKeyUp
                                ? (e) => {
                                      if (e.key === 'Enter') {
                                          onKeyUp();
                                      }
                                  }
                                : undefined
                        }
                        renderInput={(params) => {
                            const startAdornment = [];
                            if (!checkUndefinedOrNull(value)) {
                                if (value.effizienz) {
                                    startAdornment.push(
                                        <span key={'inputSelect' + finalId + '_valueCustomChild' + value.i} style={{ fontSize: height }}>
                                            <Effizienz
                                                type="step"
                                                stepHeight={typeof height === 'string' ? (height?.endsWith('em') ? parseFloat(height?.replace('em', '')) * 16 : 16) : height}
                                                step={value.i as EffizienzClass}
                                                wltp={value.effizienz.wltp}
                                            />
                                        </span>
                                    );
                                } else if (value.icon) {
                                    startAdornment.push(
                                        <span key={'inputSelect' + finalId + '_valueIcon' + value.i} style={{ fontSize: height }}>
                                            <Iconify
                                                width={!checkUndefinedNullOrEmpty(value.iconAutoSizeHeight) ? 'auto' : undefined}
                                                height={value.iconAutoSizeHeight}
                                                color={value.iconColor}
                                                icon={value.icon}
                                            />
                                        </span>
                                    );
                                } else if (value.img) {
                                    startAdornment.push(
                                        <ImageEl
                                            key={'inputSelect' + finalId + '_valueImage' + value.i}
                                            contain
                                            height={height}
                                            externalImage
                                            disablePlaceholder
                                            ratio={value.imgRatio || [4, 3]}
                                            src={value.img}
                                            alt={value.t}
                                        />
                                    );
                                } else if (value.badge) {
                                    startAdornment.push(
                                        <Badge key={'inputSelect' + finalId + '_valueBadge' + value.i} color="grey">
                                            {value.badge}
                                        </Badge>
                                    );
                                }
                            }
                            if (params.InputProps && params.InputProps.startAdornment) startAdornment.push(params.InputProps.startAdornment);
                            return (
                                <TextField
                                    {...params}
                                    key={'inputSelect' + finalId + '_autocompleteTextfield'}
                                    fullWidth
                                    error={!checkUndefinedOrNull(error)}
                                    helperText={!checkUndefinedOrNull(error) ? error : !checkUndefinedOrNull(helperText) ? helperText : undefined}
                                    autoComplete={autoComplete ? 'section-ahdcars ' + autoComplete : 'off'}
                                    label={hideLabelEdit ? '' : l}
                                    onMouseUp={!disabled && !checkUndefinedOrNull(onClick) ? onClick : undefined}
                                    onChange={(e) => {
                                        // @ts-expect-error
                                        params.inputProps.onChange(e);
                                        if (onType) onType(e);
                                    }}
                                    inputProps={{
                                        ...params.inputProps,
                                        'placeholder': hideLabelEdit ? l : '',
                                        'aria-label': l,
                                        'style': { textAlign },
                                    }}
                                    InputProps={{ ...params.InputProps, startAdornment: !checkUndefinedNullOrEmpty(startAdornment) ? startAdornment : undefined }}
                                />
                            );
                        }}
                        renderOption={(props, option, state) => {
                            const { selected } = state;
                            return (
                                // @ts-expect-error
                                <div {...props} key={`inputSelect${finalId}_autocompleteOption${option.i}`} style={{ display: 'flex', alignItems: 'center' }}>
                                    {type === 'multiselect' ? (
                                        selected ? (
                                            <CheckBox key={`inputSelect${finalId}_autocompleteOptionCheck${option.i}`} sx={{ color: 'common.white', mr: 2 }} />
                                        ) : (
                                            <CheckBoxOutlineBlank key={`inputSelect${finalId}_autocompleteOptionCheck${option.i}`} sx={{ color: 'common.white', mr: 2 }} />
                                        )
                                    ) : undefined}
                                    {(checkUndefinedNullOrEmpty(showField) || showField === 'effizienz') && option.effizienz ? (
                                        <Tooltip key={`inputSelect${finalId}_autocompleteOptionIconTooltip${option.i}`} title={option.t}>
                                            <span key={`inputSelect${finalId}_autocompleteOptionIconSpan${option.i}`} style={{ fontSize: height }}>
                                                <Effizienz
                                                    type="step"
                                                    stepHeight={typeof height === 'string' ? (height?.endsWith('em') ? parseFloat(height?.replace('em', '')) * 16 : 16) : height}
                                                    step={option.i as EffizienzClass}
                                                    wltp={option.effizienz.wltp}
                                                />
                                            </span>
                                        </Tooltip>
                                    ) : (checkUndefinedNullOrEmpty(showField) || showField === 'icon') && option.icon ? (
                                        <>
                                            <Tooltip key={`inputSelect${finalId}_autocompleteOptionIconTooltip${option.i}`} title={option.t}>
                                                <span key={`inputSelect${finalId}_autocompleteOptionIconSpan${option.i}`} style={{ fontSize: height }}>
                                                    <Iconify
                                                        width={!checkUndefinedNullOrEmpty(option.iconAutoSizeHeight) ? 'auto' : undefined}
                                                        height={option.iconAutoSizeHeight}
                                                        color={option.iconColor}
                                                        icon={option.icon}
                                                    />
                                                </span>
                                            </Tooltip>
                                            {showExtra && option.et ? (
                                                <span key={`inputSelect${finalId}_autocompleteOptionIconText${option.i}`} style={{ fontSize: height, marginLeft: 5 }}>
                                                    {option.et}
                                                </span>
                                            ) : undefined}
                                        </>
                                    ) : (checkUndefinedNullOrEmpty(showField) || showField === 'img') && option.img ? (
                                        <>
                                            <ImageEl
                                                key={`inputSelect${finalId}_autocompleteOptionImage${option.i}`}
                                                contain
                                                alt={option.t}
                                                externalImage
                                                disablePlaceholder
                                                ratio={option.imgRatio || [4, 3]}
                                                src={option.img}
                                                height={height}
                                            />
                                            {showExtra && option.et ? (
                                                <span key={`inputSelect${finalId}_autocompleteOptionImageText${option.i}`} style={{ fontSize: height, marginLeft: 5 }}>
                                                    {option.et}
                                                </span>
                                            ) : undefined}
                                        </>
                                    ) : (checkUndefinedNullOrEmpty(showField) || showField === 'badge') && option.badge ? (
                                        <>
                                            <Badge key={`inputSelect${finalId}_autocompleteOptionBadge${option.i}`} style={{ margin: 0 }} color={'grey'}>
                                                {option.badge}
                                            </Badge>
                                            {showExtra && option.et ? (
                                                <span key={`inputSelect${finalId}_autocompleteOptionText${option.i}`} style={{ fontSize: height, marginLeft: 5 }}>
                                                    {option.et}
                                                </span>
                                            ) : undefined}
                                        </>
                                    ) : (
                                        option.t + (showExtra && option.et ? ' ' + option.et : '')
                                    )}
                                    {LEAD && option.warning ? (
                                        <Tooltip key={`inputSelect${finalId}_autocompleteOptionWarning${option.i}`} title={option.warning}>
                                            <Warning style={{ marginLeft: 8 }} />
                                        </Tooltip>
                                    ) : undefined}
                                    {!LEAD && option.warningSPT ? (
                                        <Tooltip key={`inputSelect${finalId}_autocompleteOptionWarning${option.i}`} title={option.warningSPT}>
                                            <Warning style={{ marginLeft: 8 }} />
                                        </Tooltip>
                                    ) : undefined}
                                </div>
                            );
                        }}
                    />
                    {!checkUndefinedOrNull(endIcon) && (
                        <div
                            key={'inputSelect' + finalId + '_endIcon'}
                            className="materialIcon"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined,
                                marginLeft: 8,
                                marginTop: 20,
                                marginBottom: -20,
                            }}
                        >
                            <Iconify icon={endIcon} />
                        </div>
                    )}
                    {!checkUndefinedOrNull(info) && (
                        <div
                            key={'inputSelect' + finalId + '_info'}
                            className="materialIcon"
                            style={{ color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined, marginLeft: 8 }}
                        >
                            <Tooltip key={'inputSelect' + finalId + '_infoTooltip'} title={info}>
                                <span key={'inputSelect' + finalId + '_infoSpan'}>
                                    <Info key={'inputSelect' + finalId + '_infoInfo'} color="primary" />
                                </span>
                            </Tooltip>
                        </div>
                    )}
                </div>
            )}
        </div>
    ) : (
        <InputNonEditValue
            autoWidth={autoWidth}
            endIcon={endIcon}
            fatLabel={fatLabel}
            finalId={finalId}
            hideLabel={hideLabel}
            icon={icon}
            info={info}
            l={l}
            loading={loading}
            textAlign={textAlign}
            value={getValue(v)}
        />
    );
};

export default InputSelect;
